// SPDX-License-Identifier: AGPL-3.0-or-later

import { ChevronDown, LogOut, Settings, Sun } from 'lucide-react';
import { Link } from 'react-router';
import { DropdownMenu } from '@intakeform/ui';
import { useTheme, type ThemePreference } from '../theme/index.js';
import { Avatar } from './Avatar.js';
import type { MeResponse } from '../api/types.js';

export interface SidebarUserMenuProps {
  me: MeResponse;
  onSignOut: () => void;
  isSigningOut?: boolean | undefined;
}

const THEME_NEXT: Record<ThemePreference, ThemePreference> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

const THEME_LABEL: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

/**
 * Bottom-pinned user menu (in-dtt). Collapses what was three separate
 * controls — display name, theme toggle, sign-out — into a single
 * trigger button that opens a Radix-backed DropdownMenu.
 *
 * Trigger anatomy: [initials avatar] [display name + org] [chevron]
 *                  └─ Avatar.tsx ──┘ └─ stacked text ────┘ └─ down ┘
 *
 * Menu items:
 *   • Account             — link to /settings, disabled when role ≠ admin
 *   • Toggle theme        — cycles light → dark → system (announces next state)
 *   • Sign out            — destructive variant
 *
 * Accessibility:
 *   - The trigger carries `aria-haspopup='menu'` (Radix also sets it,
 *     but the bead asks for it explicitly so the markup is auditable).
 *   - The avatar is `aria-hidden`; the button's accessible name comes
 *     from the display name + org text.
 *   - Keyboard: Enter/Space opens, arrow keys navigate, Escape closes
 *     (all from Radix).
 */
export function SidebarUserMenu({ me, onSignOut, isSigningOut }: SidebarUserMenuProps) {
  const isAdmin = me.user.role === 'admin';
  const { preference, mode, setPreference } = useTheme();
  const nextPreference = THEME_NEXT[preference];

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          data-ui-sidebar-user-trigger=""
          aria-haspopup="menu"
          disabled={isSigningOut === true}
        >
          <Avatar displayName={me.user.displayName} size={28} />
          <span data-ui-sidebar-user-trigger-text="">
            <span data-ui-sidebar-user-name="">{me.user.displayName}</span>
            <span data-ui-sidebar-user-org="">{me.organization.name}</span>
          </span>
          <ChevronDown aria-hidden="true" size={16} data-ui-sidebar-user-chevron="" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" side="top" sideOffset={8} data-ui-sidebar-user-menu="">
        <DropdownMenu.Item
          asChild
          disabled={!isAdmin}
          icon={<Settings aria-hidden="true" size={16} />}
        >
          {isAdmin ? (
            <Link to="/settings">Account</Link>
          ) : (
            // Disabled menu items still render an anchor-like element
            // so the trigger anatomy stays consistent; the disabled
            // attribute on the parent prevents activation.
            <span>Account</span>
          )}
        </DropdownMenu.Item>
        <DropdownMenu.Item
          icon={<Sun aria-hidden="true" size={16} />}
          // Radix's `onSelect` runs before the menu closes — we read
          // the most recent preference here so quick cycles don't lose
          // a step.
          onSelect={(event) => {
            event.preventDefault();
            setPreference(nextPreference);
          }}
        >
          Theme · {THEME_LABEL[preference]}
          <span data-ui-sidebar-user-menu-sub="" className="muted">
            currently {mode} → switch to {THEME_LABEL[nextPreference].toLowerCase()}
          </span>
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item
          destructive
          icon={<LogOut aria-hidden="true" size={16} />}
          onSelect={() => {
            onSignOut();
          }}
        >
          Sign out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
